<script>
import { ref, toRef, watch } from 'vue';

export default {
  name: 'ScheduleMessageModal',
  props: {
    show: { type: Boolean, default: false },
    conversationId: { type: [Number, String], default: null },
    editingMessage: { type: Object, default: null },
  },
  emits: ['close', 'schedule'],
  setup(props, { emit }) {
    const editingMessage = toRef(props, 'editingMessage');
    const isEditingMessage = toRef(props, 'editingMessage');
    const scheduledAt = ref('');
    const messageContent = ref('');
    const messageTitle = ref('');

    const formatDateTimeLocal = value => {
      if (!value) return '';

      const parsedDate =
        typeof value === 'number' ? new Date(value * 1000) : new Date(value);

      if (Number.isNaN(parsedDate.getTime())) return '';

      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const hours = String(parsedDate.getHours()).padStart(2, '0');
      const minutes = String(parsedDate.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const buildFallbackTitle = content => {
      const trimmedContent = String(content || '').trim();
      if (!trimmedContent) return '';

      return trimmedContent.length > 60
        ? `${trimmedContent.slice(0, 57)}...`
        : trimmedContent;
    };

    const syncFromEditingMessage = message => {
      if (!message) {
        messageTitle.value = '';
        scheduledAt.value = '';
        messageContent.value = '';
        return;
      }

      messageTitle.value = message.title || buildFallbackTitle(message.message);
      scheduledAt.value = formatDateTimeLocal(message.scheduled_at);
      messageContent.value = message.message || '';
    };

    const onClose = () => {
      syncFromEditingMessage(null);
      emit('close');
    };

    const handleSchedule = () => {
      emit('schedule', {
        conversationId: props.conversationId,
        messageId: editingMessage.value?.id || null,
        title: messageTitle.value || buildFallbackTitle(messageContent.value),
        scheduledAt: scheduledAt.value,
        content: messageContent.value,
      });
      onClose();
    };

    watch(
      editingMessage,
      newMessage => {
        syncFromEditingMessage(newMessage);
      },
      { immediate: true }
    );

    watch(
      () => props.show,
      isVisible => {
        if (!isVisible) return;

        syncFromEditingMessage(editingMessage.value);
      }
    );

    return {
      scheduledAt,
      messageContent,
      messageTitle,
      isEditingMessage,
      onClose,
      handleSchedule,
    };
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col p-6">
      <h2 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
        {{
          isEditingMessage
            ? $t('KANBAN.SCHEDULE_MESSAGE.EDIT_TITLE')
            : $t('KANBAN.SCHEDULE_MESSAGE.TITLE')
        }}
      </h2>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          {{ $t('KANBAN.SCHEDULE_MESSAGE.SUBJECT_LABEL') }}
        </label>
        <input
          v-model="messageTitle"
          type="text"
          class="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm dark:bg-slate-800"
          :placeholder="$t('KANBAN.SCHEDULE_MESSAGE.SUBJECT_PLACEHOLDER')"
        />
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          {{ $t('KANBAN.SCHEDULE_MESSAGE.DATE_LABEL') }}
        </label>
        <input
          v-model="scheduledAt"
          type="datetime-local"
          class="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm dark:bg-slate-800"
        />
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          {{ $t('KANBAN.SCHEDULE_MESSAGE.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="messageContent"
          rows="4"
          class="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm dark:bg-slate-800"
          :placeholder="$t('KANBAN.SCHEDULE_MESSAGE.PLACEHOLDER')"
        />
      </div>

      <div class="flex justify-end gap-2">
        <woot-button variant="clear" @click="onClose">
          {{ $t('KANBAN.CANCEL') }}
        </woot-button>
        <woot-button
          :disabled="!scheduledAt || !messageContent"
          @click="handleSchedule"
        >
          {{
            isEditingMessage
              ? $t('KANBAN.SCHEDULE_MESSAGE.UPDATE')
              : $t('KANBAN.SCHEDULE_MESSAGE.SUBMIT')
          }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
